import { T } from 'src/shared/helpers';
import SelectionInspector from './selectionInspector';
import { FrameSelectionIcon } from '../../../../../widgets/icons/scene';
import SelectAllIcon from 'src/shared/material_icons/image/filter_center_focus.svg';
import { CamerasRailIcon, FreeCameraIcon } from './sceneRailIcons';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
// «Add camera» eredita SAVE: qui una camera non si aggiunge, si cattura — si
// inquadra e si memorizza la posizione corrente. Il glifo è quello che aggiunge.
import { DuplicateIcon as CloneIcon, PlusIcon as SaveIcon, TrashIcon as RemoveIcon } from '../../../../../widgets/icons/scene';
import OverlayPanel from '../overlayPanel';
import { BaseTreeNode, TreeComponent, TreeHandle, useTreeNodeManager } from '../tree';
import CameraLocationOverlayComponent from './overlay/CameraLocationOverlayComponent';
import CameraSettingOverlayComponent from './overlay/CameraSettingOverlayComponent';
import { CAMERA_DEFAULTS, CameraProperty as SceneToolbarProperty } from './cameraEditorConfig';
import SectionPanel from './sectionPanel';
import { useRemovalPrompt } from './confirmRemoval';
import { freeName, useBornSubject } from './hooks/useBornSubject';
import { SceneEditorAction } from './sceneEditorActionBar';

// LE IMPOSTAZIONI DELLE CAMERE SONO UN CAPITOLO, non una camera. Stanno sopra la lista e non
// dentro, perché non sono una riga fra le righe: campo visivo, zoom e limiti valgono per tutta la
// scena. Ma si SCEGLIE come una riga — stessa altezza, stesso segno a sinistra quando è la cosa
// che stai guardando — perché il gesto è quello, e un gesto uguale si veste uguale.
//
// Prima era una fascia grigia coi colori scritti in chiaro: in tema scuro restava chiara.
// LA RIGA DELLA CAMERA LIBERA E L'ELENCO SI DIVIDONO L'ALTEZZA. L'albero è alto almeno quanto il
// suo contenitore (è lì che il vuoto deseleziona): con la riga sopra, sbordava della sua altezza e
// la colonna mostrava una barra di scorrimento senza niente da scorrere.
const CamerasColumn = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const CamerasTree = styled.div`
    flex: 1;
    min-height: 0;
`;

const CameraSettings = styled.button<{ $open: boolean }>`
    appearance: none;
    width: 100%;
    min-height: 34px;
    padding: 4px 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    text-align: left;
    font: inherit;
    color: inherit;
    border: none;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    border-left: 2px solid ${(props) => (props.$open ? props.theme.app.secondary : 'transparent')};
    background: ${(props) => (props.$open ? `${props.theme.app.secondary}14` : 'transparent')};

    &:hover {
        background: ${(props) => `${props.theme.app.secondary}0d`};
    }
`;

const CameraSettingsGlyph = styled.span`
    display: inline-flex;
    align-items: center;
    flex: none;
    width: 16px;
    height: 16px;
    opacity: 0.72;

    svg {
        width: 100%;
        height: 100%;
    }

    svg:not([stroke]) {
        fill: currentColor;
    }
`;

// COME SI CHIAMANO GLI ASSI SULLA CAMERA SALVATA: `px py pz` dove sta, `tx ty tz` cosa guarda.
// Sono i nomi del motore, e stanno qui perché è l'unico punto che li scrive.
const CAMERA_FIELDS = { position: ['px', 'py', 'pz'], target: ['tx', 'ty', 'tz'] } as const;
const AXIS_INDEX = { x: 0, y: 1, z: 2 } as const;

// Estendi BaseTreeNode per includere le proprietà specifiche della camera
export interface SceneCameraNode extends BaseTreeNode {
    /** Il glifo della riga: una camera, non la mesh su cui l'albero ricadrebbe. */
    customIcon?: React.ReactNode;
    /**
     * TUTTO QUELLO CHE UNA CAMERA SALVATA HA: dov'è e cosa guarda. Il motore ne conserva sette
     * campi in tutto — identificativo, nome, `px py pz`, `tx ty tz` — e niente altro. Campo
     * visivo, zoom, limiti e rotazione automatica NON sono suoi: sono impostazioni della scena,
     * e vivono nel pannello che apre «Free camera».
     */
    data: {
        position: [number, number, number];
        target: [number, number, number];
    };
    nodeType?: 'camera' | 'light' | 'object' | string;
    // Proprietà per controllare il comportamento del tree
    disableOverlay?: boolean;
    notTurnableIntoFolder?: boolean;
    // Riferimento all'oggetto camera location originale
    originalCameraLocation?: any;
}

interface CameraEditorProps {
    treeRef: React.RefObject<TreeHandle>;
    cameraLocations?: any[];
    selectedCameraLocations?: any[];
    sceneEditor?: any;
    zkScene?: any;
    onCameraLocationsUpdated?: (cameraLocations: any[]) => void;
    onInspectorChange?: (component: React.ReactNode | null) => void;
    /** «Dove sta?» per la camera aperta: passa dallo Scene Editor, perché il pannello vive più a lungo di questo elenco. */
    onRevealCamera?: (rowID: string, asRow?: boolean) => void;
    /** «Dove sta?» chiesto da fuori: la riga da portare in vista e far lampeggiare. Con `asRow`, `dataID` è la riga. */
    whereRequest?: { dataID: string; asRow?: boolean; token: number } | null;
    /** La domanda è arrivata all'albero: chi l'ha fatta la può dimenticare. */
    onWhereServed?: (token: number) => void;
}

const CameraEditor: FC<CameraEditorProps> = ({
    treeRef,
    cameraLocations = [],
    selectedCameraLocations = [],
    sceneEditor,
    zkScene,
    onCameraLocationsUpdated,
    onInspectorChange,
    onRevealCamera,
    whereRequest,
    onWhereServed
}) => {
    const [showOverlay, setShowOverlay] = useState(false);
    const [playedCamera, setPlayedCamera] = useState<string | null>(null);

    // I VALORI SI LEGGONO DALLA CAMERA, non si inventano. Qui prima stavano dei numeri fissi —
    // `fov: 1`, posizione `[0, 0, 1]` — messi per riempire la forma di un nodo, e il pannello li
    // mostrava come se fossero una lettura. Un dato inventato che si presenta come misurato è
    // peggio di un dato che manca.
    const convertCameraLocationToNode = useCallback((cameraLocation: any): SceneCameraNode => {
        const read = (attribute: string) => (typeof cameraLocation?.get === 'function' ? cameraLocation.get(attribute) : cameraLocation?.[attribute]);
        const number = (value: any) => (typeof value === 'number' ? value : 0);

        const name = read('name') || 'Unnamed Camera';
        const id = read('locationID');
        const nodeId = id ? id.toString() : `camera-${Date.now()}`;

        return {
            id: nodeId,
            name,
            nodeType: 'camera',

            // UNA CAMERA NON È UN CUBO. Senza un glifo dichiarato la riga ricade su quello dell'albero —
            // che per una foglia è la mesh — e la lista delle camere mostrava sei cubetti. La specie non
            // porta tinta: nel prototipo la camera è l'unica che non ne ha, perché non c'è una seconda
            // specie da cui distinguerla in quella lista.
            customIcon: <CamerasRailIcon />,
            data: {
                position: [number(read('px')), number(read('py')), number(read('pz'))],
                target: [number(read('tx')), number(read('ty')), number(read('tz'))]
            },
            disableOverlay: false,
            notTurnableIntoFolder: true,
            // Manteniamo il riferimento all'oggetto originale per operazioni future
            originalCameraLocation: cameraLocation
        };
    }, []);

    // Converti le camera locations in nodi utilizzabili
    const [realCameraNodes, setRealCameraNodes] = useState<SceneCameraNode[]>([]);

    // Funzione per aggiornare i nodi manualmente
    const refreshCameraNodes = useCallback(() => {
        if (zkScene) {
            const updatedCameraLocations = zkScene.get('cameraLocations')?.models || [];
            const convertedNodes = updatedCameraLocations.map(convertCameraLocationToNode);
            setRealCameraNodes(convertedNodes);

            // Notifica anche il parent component
            if (onCameraLocationsUpdated) {
                onCameraLocationsUpdated(updatedCameraLocations);
            }
        }
    }, [zkScene, convertCameraLocationToNode, onCameraLocationsUpdated]);

    // Aggiorna i nodi quando cambiano le camera locations
    useEffect(() => {
        const convertedNodes = (cameraLocations || []).map(convertCameraLocationToNode);
        setRealCameraNodes(convertedNodes);
    }, [cameraLocations, convertCameraLocationToNode]);

    // LE IMPOSTAZIONI DELLE CAMERE SI LEGGONO DA UN POSTO SOLO, e si leggono con i metodi che il
    // motore DICHIARA — `getCameraPositionScale`, `getCameraMinZoomPerc` e compagni — invece di
    // indovinare i nomi degli attributi dentro il suo modello. Prima erano due letture diverse, una
    // per lo stato iniziale e una per la risincronizzazione, e non leggevano le stesse cose: «Zoom
    // limit» si SCRIVEVA su `limitLowerRadius` e si LEGGEVA da `zoomEnabled`, e l'altezza non si
    // leggeva affatto — nasceva sempre a zero.
    //
    // E LA PERCENTUALE SI CONVERTE IN TUTT'E DUE I VERSI. Il motore tiene lo zoom come frazione,
    // il cursore lo mostra in centesimi: la conversione c'era scrivendo e mancava leggendo, quindi
    // un minimo del 64% tornava indietro come 0,64 e il cursore — che conta per unità — lo mostrava
    // come 1. È la stessa regola che i limiti di rotazione applicano ai gradi, e infatti quelli
    // sopravvivevano.
    const readSceneCamera = useCallback(() => {
        const number = (value: any, fallback: number) => (typeof value === 'number' ? value : fallback);
        const percent = (value: any, fallback: number) => (typeof value === 'number' ? Math.round(value * 100) : fallback);

        return {
            fov: number(zkScene?.getCameraPositionFOV?.(), CAMERA_DEFAULTS.fov),
            height: number(zkScene?.getCameraPositionScale?.(), CAMERA_DEFAULTS.height),
            enableZoomLimit: zkScene?.getCameraLimitLowerRadius?.() ?? false,
            limitLowerRadius: zkScene?.getCameraLimitLowerRadius?.() ?? false,
            useMinZoomPerc: zkScene?.getCameraUseMinZoomPerc?.() ?? false,
            useMaxZoomPerc: zkScene?.getCameraUseMaxZoomPerc?.() ?? false,
            minZoomPerc: percent(zkScene?.getCameraMinZoomPerc?.(), CAMERA_DEFAULTS.minZoom * 100),
            maxZoomPerc: percent(zkScene?.getCameraMaxZoomPerc?.(), CAMERA_DEFAULTS.maxZoom * 100),

            // I LIMITI DI ROTAZIONE RESTANO IN RADIANTI qui: è il pannello a mostrarli in gradi, e la
            // conversione la fa già lui in tutt'e due i versi.
            lowerAlphaLimit: zkScene?.getCameraLowerAlphaLimit?.(),
            upperAlphaLimit: zkScene?.getCameraUpperAlphaLimit?.(),
            lowerBetaLimits: zkScene?.getCameraLowerBetaLimit?.(),
            upperBetaLimits: zkScene?.getCameraUpperBetaLimit?.()
        };
    }, [zkScene]);

    // Tornando in questa sezione dopo esserne usciti, i valori sono quelli della scena.
    useEffect(() => {
        if (!zkScene) return;

        setCameraSettings((prev) => ({ ...prev, ...readSceneCamera() }));
    }, [zkScene, readSceneCamera]);

    // TRE PONTI VERSO IL CORPO DEL COMPONENTE. La fabbrica del pannello nasce PRIMA dell'albero che
    // la userà — è lui a riceverla — quindi da qui dentro non si vedono né lo stato della lista né le
    // funzioni che lo cambiano. I riferimenti si riempiono subito sotto, ed è il motivo per cui la
    // fabbrica può non dipendere da niente: si rifà solo quando cambia la camera, non a ogni respiro.
    const updateNodeRef = useRef<((id: string, updates: Partial<SceneCameraNode>) => void) | null>(null);
    const playedCameraRef = useRef<string | null>(null);
    const sceneEditorRef = useRef<any>(null);
    // E uno verso lo Scene Editor: la freccetta «dove sta» del pannello.
    const revealCameraRef = useRef(onRevealCamera);

    playedCameraRef.current = playedCamera;
    sceneEditorRef.current = sceneEditor;
    revealCameraRef.current = onRevealCamera;

    // LA SCHEDA DI UNA CAMERA. Prima non ce n'era una: questa fabbrica restituiva `null`, quindi
    // scegliere una riga lasciava la colonna di destra vuota — e la sezione era l'unica delle quattro
    // in cui un soggetto scelto non si poteva guardare.
    const createNodeOverlay = useCallback((node: SceneCameraNode) => {
        return () => (
            <CameraLocationOverlayComponent
                name={node.name}
                camera={node.data}
                // La riga di una camera è il suo `locationID`: è quello che si chiede di ritrovare.
                onReveal={() => revealCameraRef.current?.(node.id, true)}
                onRename={(name) => {
                    node.originalCameraLocation?.set?.('name', name);
                    updateNodeRef.current?.(node.id, { name });
                }}
                onChange={(what, axis, value) => {
                    const index = AXIS_INDEX[axis];

                    // IL NUMERO SI SCRIVE SULLA CAMERA, che è dove vive e dove sopravvive al salvataggio.
                    // E POI SI DICE ALLO STATO: la sola mutazione cambierebbe un oggetto che nessuno sta
                    // guardando, e il pannello resterebbe fermo sul valore di prima.
                    node.originalCameraLocation?.set?.(CAMERA_FIELDS[what][index], value);

                    const next = [...node.data[what]] as [number, number, number];
                    next[index] = value;
                    updateNodeRef.current?.(node.id, { data: { ...node.data, [what]: next } });

                    // SE STAI GUARDANDO ATTRAVERSO QUESTA CAMERA, la vista segue il numero che tocchi.
                    // Senza animazione: qui si sta regolando, e un movimento morbido a ogni cifra
                    // arriverebbe sempre in ritardo su quello che stai facendo.
                    if (playedCameraRef.current === node.id) sceneEditorRef.current?.setCameraLocation?.(node.id, false, false);
                }}
            />
        );
    }, []);

    // Usa l'hook per la gestione dei nodi con i dati reali
    const { filter, setFilter, selectedIds, filteredData, findNodeById, treeState, setSelectedIds, updateNode } = useTreeNodeManager<SceneCameraNode>(
        realCameraNodes,
        createNodeOverlay
    );

    updateNodeRef.current = updateNode;

    // LA SELEZIONE È QUELLA DI TUTTI. Qui era riscritta a mano, e con una differenza che si
    // vedeva: ⇧ AGGIUNGEVA invece di prendere un intervallo pulito, cioè faceva il mestiere di ⌘.
    // Un tasto non può voler dire due cose nella stessa applicazione.

    // La riga nuova compare solo dopo che la scena è stata riletta: si dichiara il nome che sta
    // per comparire, e quando compare diventa il soggetto — col nome già in scrittura.
    const expectCamera = useBornSubject(realCameraNodes, (born) => {
        setSelectedIds([born.id]);
        treeRef.current?.startRename(born.id);
    });

    // Con un filtro attivo l'albero mostra ciò che resta, non tutto.
    const treeStateForComponent = useMemo(() => ({ ...treeState, nodes: filter ? filteredData : treeState.nodes }), [treeState, filteredData, filter]);

    // ANDARE A GUARDARE ATTRAVERSO UNA CAMERA lo fa il mirino della riga, e solo lui (QA, 2026-10-12).
    // Scegliere una riga la porta nell'Inspector e basta: prima la sceglieva E ci portava la vista, e
    // chi stava lavorando nel palco se lo trovava girato solo per aver letto una camera.
    const handlePlayCamera = (cameraLocationId: string) => {
        sceneEditor.setCameraLocation(cameraLocationId);
        setPlayedCamera(cameraLocationId);
    };

    // «DOVE STA?» — la riga si porta in vista e lampeggia, senza cambiare né la scelta né il pannello.
    // A una domanda si risponde una volta: lo si dice a chi l'ha fatta.
    const [blinkRequest, setBlinkRequest] = useState<{ nodeId?: string; dataID?: string; token: number } | null>(null);

    useEffect(() => {
        if (!whereRequest) return;

        const target = whereRequest.asRow ? { nodeId: whereRequest.dataID } : { dataID: whereRequest.dataID };
        setBlinkRequest((previous) => ({ ...target, token: (previous?.token ?? 0) + 1 }));
        onWhereServed?.(whereRequest.token);
    }, [whereRequest, onWhereServed]);

    const [cameraSettings, setCameraSettings] = useState(() => ({
        id: 'camera-settings',
        name: T._('Free camera', 'Admin'),

        // NON SONO DI UNA CAMERA e non fingono di esserlo: `InspectorShell` le marca «Cameras». Questi
        // due campi restano perché il pannello è nato dalla forma di una camera, e nessuno li mostra.
        position: [0, 0, 5] as [number, number, number],
        target: [0, 0, 0] as [number, number, number],
        isActive: false,

        ...readSceneCamera()
    }));

    // UN SOGGETTO PER VOLTA nella colonna di destra. Le impostazioni e una camera sono due soggetti
    // diversi: aprire le une lascia la lista senza righe prese, e scegliere una riga chiude le une.
    // Senza, restavano accese tutt'e due e la colonna mostrava quella che aveva pubblicato per
    // ultima — cioè dipendeva dall'ordine, che è il modo peggiore di decidere.
    const toggleCameraSettings = () => {
        const opening = !showOverlay;

        if (opening) {
            setSelectedIds([]);
            treeState.closeOverlay();
        }

        setShowOverlay(opening);
    };

    useEffect(() => {
        if (selectedIds.length) setShowOverlay(false);
    }, [selectedIds]);

    const closeCameraSettings = () => {
        setShowOverlay(false);
        onInspectorChange?.(null);
    };

    useEffect(() => {
        if (showOverlay && onInspectorChange) {
            onInspectorChange(<CameraSettingOverlayComponent camera={cameraSettings} onPropertyChange={handleCameraPropertyChange} />);
        }
        // The inspector is intentionally refreshed only when its camera data changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cameraSettings, showOverlay]);

    const handleCameraPropertyChange = (property: SceneToolbarProperty, value?: any) => {
        // Aggiorna lo stato locale in base alla proprietà
        switch (property) {
            case SceneToolbarProperty.CAMERA_POSITION_FOV:
                setCameraSettings((prev) => ({ ...prev, fov: value }));
                sceneEditor?.setCameraPositionFOV?.(value);
                break;
            case SceneToolbarProperty.CAMERA_HEIGHT:
                setCameraSettings((prev) => ({ ...prev, height: value }));
                sceneEditor?.setCameraPositionScale(value);
                break;
            case SceneToolbarProperty.CAMERA_LIMIT_ZOOM:
                setCameraSettings((prev) => ({ ...prev, enableZoomLimit: value }));
                sceneEditor?.setCameraLimitLowerRadius?.(value);
                if (!value) {
                    // Se disattivo zoom limit, disattivo anche tutti i sotto-controlli
                    sceneEditor?.setCameraUseMinZoomPercentage?.(false);
                    sceneEditor?.setCameraUseMaxZoomPercentage?.(false);
                    setCameraSettings((prev) => ({ ...prev, useMinZoomPerc: false, useMaxZoomPerc: false }));
                }
                break;

            case SceneToolbarProperty.CAMERA_ENABLE_LIMIT_MIN_ZOOM:
                setCameraSettings((prev) => ({ ...prev, useMinZoomPerc: value }));
                sceneEditor?.setCameraUseMinZoomPercentage?.(value);
                break;

            case SceneToolbarProperty.CAMERA_ENABLE_LIMIT_MAX_ZOOM:
                setCameraSettings((prev) => ({ ...prev, useMaxZoomPerc: value }));
                sceneEditor?.setCameraUseMaxZoomPercentage?.(value);
                break;

            case SceneToolbarProperty.CAMERA_LIMIT_MIN_ZOOM:
                setCameraSettings((prev) => ({ ...prev, minZoomPerc: value * 100 })); // Converti da 0-1 a 0-100
                sceneEditor?.setCameraMinZoomPercentage?.(value);
                break;

            case SceneToolbarProperty.CAMERA_LIMIT_MAX_ZOOM:
                setCameraSettings((prev) => ({ ...prev, maxZoomPerc: value * 100 })); // Converti da 0-1 a 0-100
                sceneEditor?.setCameraMaxZoomPercentage?.(value);
                break;

            case SceneToolbarProperty.CAMERA_LOCK_HORIZONTAL:
                // Gestisce l'abilitazione/disabilitazione dei limiti orizzontali
                sceneEditor?.setCameraUseLowerAlphaLimit?.(value);
                sceneEditor?.setCameraUseUpperAlphaLimit?.(value);

                // Aggiungi i metodi per il lock/unlock come nel vecchio sistema
                if (value) {
                    sceneEditor?.lockCameraAlphaAngle?.();

                    // Quando attivo, usa i valori esistenti dal sceneConfig o imposta valori di default ragionevoli
                    const sceneConfig = sceneEditor?.getSceneConfig?.();
                    const cameraConfig = sceneConfig?.get?.('camera');

                    const currentLowerAlpha = cameraConfig?.get?.('lowerAlphaLimit');
                    const currentUpperAlpha = cameraConfig?.get?.('upperAlphaLimit');

                    // Imposta valori di default solo se non esistono già, con un range ragionevole
                    const defaultLowerAlpha = currentLowerAlpha ?? -Math.PI; // -180 gradi in radianti
                    const defaultUpperAlpha = currentUpperAlpha ?? Math.PI; // +180 gradi in radianti

                    setCameraSettings((prev) => ({
                        ...prev,
                        lowerAlphaLimit: defaultLowerAlpha,
                        upperAlphaLimit: defaultUpperAlpha
                    }));

                    // Imposta i valori solo se non esistevano già
                    if (currentLowerAlpha == null) {
                        sceneEditor?.setViewerCameraLowerAlphaLimit?.(defaultLowerAlpha);
                    }
                    if (currentUpperAlpha == null) {
                        sceneEditor?.setViewerCameraUpperAlphaLimit?.(defaultUpperAlpha);
                    }
                } else {
                    sceneEditor?.unlockCameraAlphaAngle?.();

                    // Quando disattivo, resetto i valori a undefined per far scomparire i controlli
                    setCameraSettings((prev) => ({
                        ...prev,
                        lowerAlphaLimit: undefined,
                        upperAlphaLimit: undefined
                    }));
                }
                break;

            case SceneToolbarProperty.CAMERA_LOCK_VERTICAL:
                // Gestisce l'abilitazione/disabilitazione dei limiti verticali
                sceneEditor?.setCameraUseLowerBetaLimit?.(value);
                sceneEditor?.setCameraUseUpperBetaLimit?.(value);

                // Aggiungi i metodi per il lock/unlock come nel vecchio sistema
                if (value) {
                    sceneEditor?.lockCameraBetaAngle?.();

                    // Quando attivo, usa i valori esistenti dal sceneConfig o imposta valori di default ragionevoli
                    const sceneConfig = sceneEditor?.getSceneConfig?.();
                    const cameraConfig = sceneConfig?.get?.('camera');

                    const currentLowerBeta = cameraConfig?.get?.('lowerBetaLimit');
                    const currentUpperBeta = cameraConfig?.get?.('upperBetaLimit');

                    // Imposta valori di default solo se non esistono già, con un range ragionevole
                    const defaultLowerBeta = currentLowerBeta ?? (0 + 90) * (Math.PI / 180); // -90 gradi in radianti
                    const defaultUpperBeta = currentUpperBeta ?? (180 + 90) * (Math.PI / 180); // +90 gradi in radianti

                    setCameraSettings((prev) => ({
                        ...prev,
                        lowerBetaLimits: defaultLowerBeta,
                        upperBetaLimits: defaultUpperBeta
                    }));

                    // Imposta i valori solo se non esistevano già
                    if (currentLowerBeta == null) {
                        sceneEditor?.setViewerCameraLowerBetaLimit?.(defaultLowerBeta);
                    }
                    if (currentUpperBeta == null) {
                        sceneEditor?.setViewerCameraUpperBetaLimit?.(defaultUpperBeta);
                    }
                } else {
                    sceneEditor?.unlockCameraBetaAngle?.();

                    // Quando disattivo, resetto i valori a undefined per far scomparire i controlli
                    setCameraSettings((prev) => ({
                        ...prev,
                        lowerBetaLimits: undefined,
                        upperBetaLimits: undefined
                    }));
                }
                break;

            case SceneToolbarProperty.CAMERA_MIN_ALPHA:
                setCameraSettings((prev) => ({ ...prev, lowerAlphaLimit: value }));
                sceneEditor?.setViewerCameraLowerAlphaLimit?.(value);
                sceneEditor?.setCameraUseLowerAlphaLimit?.(true);
                break;

            case SceneToolbarProperty.CAMERA_MAX_ALPHA:
                setCameraSettings((prev) => ({ ...prev, upperAlphaLimit: value }));
                sceneEditor?.setViewerCameraUpperAlphaLimit?.(value);
                sceneEditor?.setCameraUseUpperAlphaLimit?.(true);
                break;

            case SceneToolbarProperty.CAMERA_MIN_BETA:
                setCameraSettings((prev) => ({ ...prev, lowerBetaLimits: value }));
                sceneEditor?.setViewerCameraLowerBetaLimit?.(value);
                sceneEditor?.setCameraUseLowerBetaLimit?.(true);
                break;

            case SceneToolbarProperty.CAMERA_MAX_BETA:
                setCameraSettings((prev) => ({ ...prev, upperBetaLimits: value }));
                sceneEditor?.setViewerCameraUpperBetaLimit?.(value);
                sceneEditor?.setCameraUseUpperBetaLimit?.(true);
                break;

            case SceneToolbarProperty.CAMERA_RESET:
                // RIMETTERE A POSTO PASSA DALLA STESSA PORTA di ogni altra modifica: scrivere qui una
                // seconda volta nel motore vorrebbe dire tenere due strade per la stessa cosa, e prima o
                // poi una delle due impara qualcosa che l'altra non sa.
                //
                // L'ORDINE CONTA: prima i valori, poi gli interruttori. Spegnere «Zoom limit» spegne
                // anche i due che stanno sotto di lui, e farlo per ultimo garantisce che restino spenti.
                handleCameraPropertyChange(SceneToolbarProperty.CAMERA_POSITION_FOV, CAMERA_DEFAULTS.fov);
                handleCameraPropertyChange(SceneToolbarProperty.CAMERA_HEIGHT, CAMERA_DEFAULTS.height);
                handleCameraPropertyChange(SceneToolbarProperty.CAMERA_LIMIT_MIN_ZOOM, CAMERA_DEFAULTS.minZoom);
                handleCameraPropertyChange(SceneToolbarProperty.CAMERA_LIMIT_MAX_ZOOM, CAMERA_DEFAULTS.maxZoom);
                handleCameraPropertyChange(SceneToolbarProperty.CAMERA_LIMIT_ZOOM, false);
                handleCameraPropertyChange(SceneToolbarProperty.CAMERA_LOCK_HORIZONTAL, false);
                handleCameraPropertyChange(SceneToolbarProperty.CAMERA_LOCK_VERTICAL, false);
                break;

            default:
        }
    };

    // NELLA BARRA RESTA CIÒ CHE NON HA UN SOGGETTO: «Add camera» salva la vista corrente, che una
    // riga non ce l'ha ancora. Rinominare e rimuovere sono scesi nel «…» della riga.
    // Una sola conferma per togliere, in tutte le liste: il testo lo compone lei.
    const askRemove = useRemovalPrompt();

    const selectableIds: string[] = treeState.flatVisibleList.map((node: any) => node.id);
    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));

    const tools: SceneEditorAction[] = [
        {
            name: T._('Add', 'Admin'),
            tooltip: T._('The current view, saved', 'Admin'),
            icon: <SaveIcon />,
            kind: 'add',
            // LA CAMERA NASCE SUBITO. Prima si apriva un modulo che chiedeva il nome: ma qui il
            // nome non è una decisione — la vista è già inquadrata, e «Add» deve salvarla — mentre
            // una finestra fra il clic e la cosa fa perdere proprio quello che si voleva tenere.
            // Nasce con un nome libero, diventa il soggetto, e lo si cambia dove lo si legge.
            onClick: () => {
                closeCameraSettings();

                if (!sceneEditor || !sceneEditor.saveCameraLocation) {
                    console.warn('📷 sceneEditor.saveCameraLocation not available');
                    return;
                }

                const name = freeName(realCameraNodes, T._('New camera', 'Admin'));
                sceneEditor.saveCameraLocation(name);

                // Piccolo delay per permettere al sistema di aggiornare
                setTimeout(() => {
                    refreshCameraNodes();
                }, 100);

                expectCamera(name);
            }
        },
        {
            // IL TASTO RESTA ACCANTO ALLA SCORCIATOIA, non al suo posto (call coi colleghi,
            // 2026-09-09): CTRL+A è per chi la sa, il tasto è per chi guarda. Due porte per la
            // stessa cosa vanno bene finché la cosa è una sola — e qui lo è.
            name: allSelected ? T._('Deselect all', 'Admin') : T._('Select all', 'Admin'),
            tooltip: T._('Everything visible in this list', 'Admin'),
            icon: <SelectAllIcon />,
            kind: 'action',
            active: allSelected,
            disabled: selectableIds.length === 0,
            onClick: () => setSelectedIds(allSelected ? [] : selectableIds)
        }
    ];

    // UN MOTORE, DUE PORTE — come nelle altre liste: il «…» passa la riga o, su una riga scelta
    // insieme ad altre, la selezione; il pannello del mucchio passa le righe prese.
    const subjectCommands = useCallback(
        (ids: string[]): SceneEditorAction[] => {
            if (!ids.length) return [];

            const commands: SceneEditorAction[] = [];

            // RINOMINARE NON STA PIÙ QUI: il nome si cambia sulla riga, come in tutte le altre liste.
            // E qui non era solo scomodo — era distruttivo. La finestra rinominava TOGLIENDO la camera
            // e risalvandola, e `saveCameraLocation` salva l'inquadratura CORRENTE: rinominare una
            // camera mentre si guardava altrove ne riscriveva silenziosamente la posizione.
            commands.push({
                name: T._('Clone', 'Admin'),
                tooltip: T._('A copy in this scene', 'Admin'),
                icon: <CloneIcon />,
                kind: 'action',
                onClick: () => {
                    // DUPLICARE UNA CAMERA IL MOTORE NON LO SA FARE — `cloneCameraLocation` non esiste —
                    // ma non serve che lo sappia: una camera salvata è sei numeri e un nome. La si fa
                    // nascere con «salva la vista corrente», e poi le si riscrivono addosso i sei numeri
                    // dell'originale.
                    //
                    // COSÌ LA VISTA NON SI MUOVE. La strada ovvia — andare sulla camera da copiare e poi
                    // salvare — darebbe lo stesso risultato portandoti però altrove: duplicare è un gesto
                    // sulla lista, non sul palco.
                    ids.forEach((id) => {
                        const node = findNodeById(id);
                        const source = node?.originalCameraLocation;
                        if (!source || !sceneEditor?.saveCameraLocation) return;

                        const name = freeName(realCameraNodes, `${node.name} - copy`);
                        sceneEditor.saveCameraLocation(name);

                        const born = (zkScene?.get('cameraLocations')?.models || []).find((location: any) => location?.get?.('name') === name);
                        if (!born) return;

                        [...CAMERA_FIELDS.position, ...CAMERA_FIELDS.target].forEach((field) => born.set(field, source.get(field)));
                    });

                    setTimeout(() => {
                        refreshCameraNodes();
                    }, 100);
                }
            });

            commands.push({
                name: T._('Remove', 'Admin'),
                tooltip: T._('Out of this scene', 'Admin'),
                icon: <RemoveIcon />,
                kind: 'danger',
                onClick: () => {
                    closeCameraSettings();
                    // Il pannello si chiude solo se si conferma: chi rinuncia lo ritrova com'era.
                    askRemove({
                        names: ids.map((id) => findNodeById(id)?.name).filter((name): name is string => !!name),
                        one: T._('camera', 'Admin'),
                        many: T._('cameras', 'Admin'),
                        onConfirm: () => {
                            treeState.closeOverlay();
                            // Rimuovi le camera locations dal SceneEditor
                            ids.forEach((id) => {
                                const node = findNodeById(id);
                                if (node?.originalCameraLocation && sceneEditor?.removeCameraLocation) {
                                    sceneEditor.removeCameraLocation(node.originalCameraLocation);
                                }
                            });

                            // Guardare attraverso una camera che non c'è più non è uno stato: si torna
                            // alla prospettiva libera.
                            if (ids.includes(playedCamera!)) {
                                setPlayedCamera(null);
                            }

                            setSelectedIds([]);

                            // Aggiorna immediatamente i nodi
                            setTimeout(() => {
                                refreshCameraNodes();
                            }, 100);
                        }
                    });
                }
            });

            return commands;
        },
        [closeCameraSettings, treeState, findNodeById, sceneEditor, refreshCameraNodes, playedCamera, setPlayedCamera, setSelectedIds, askRemove]
    );

    // I comandi sono funzioni nuove a ogni render: l'effetto qui sotto non può dipenderne o
    // riparte all'infinito (manda un elemento nuovo all'Inspector, che rende di nuovo l'editor,
    // che rifà i comandi). Guarda la selezione, e i comandi li legge da qui.
    const subjectCommandsRef = useRef(subjectCommands);
    subjectCommandsRef.current = subjectCommands;

    // Con più di una riga presa, l'Inspector guarda il mucchio: stessa regola delle altre liste.
    useEffect(() => {
        if (!onInspectorChange || selectedIds.length < 2) return;

        const entries = selectedIds
            .map((id) => findNodeById(id))
            .filter((node): node is NonNullable<typeof node> => node != null)
            .map((node) => ({
                id: node.id,
                name: node.name,
                icon: <CamerasRailIcon />,
                // Una voce del badge prende quella sola, come un clic sulla sua riga.
                onPick: () => treeRef.current?.selectAndOpenOverlay(node.id)
            }));

        onInspectorChange(<SelectionInspector entries={entries} actions={subjectCommandsRef.current(selectedIds)} icon={<CamerasRailIcon />} tone='camera' />);
    }, [selectedIds, findNodeById, onInspectorChange, treeRef]);

    return (
        <SectionPanel
            label={T._('Scene cameras', 'Admin')}
            actions={tools}
            selectionHint={selectableIds.length > 1}
            filter={filter}
            onFilterChange={setFilter}
            searchPlaceholder={T._('Search cameras…', 'Admin')}
        >
            <CamerasColumn>
                <CameraSettings type='button' $open={showOverlay} aria-pressed={showOverlay} onClick={toggleCameraSettings}>
                    <CameraSettingsGlyph aria-hidden='true'>
                        <FreeCameraIcon />
                    </CameraSettingsGlyph>
                    {T._('Free camera', 'Admin')}
                </CameraSettings>

                <CamerasTree>
                    <TreeComponent
                        ref={treeRef}
                        highlight={filter}
                        treeState={treeStateForComponent}
                        treeLabel='Scene cameras'
                        blinkRequest={blinkRequest}
                        emptyIcon={<CamerasRailIcon />}
                        emptyMessage={
                            filter.trim() ? T._('No camera matches the search.', 'Admin') : T._('No cameras yet. Add one to save a point of view.', 'Admin')
                        }
                        /* L'OCCHIO NON C'È, e non è una dimenticanza: una camera salvata non viene resa, quindi
                   non c'è niente da mostrare o nascondere. Il mockup ce l'ha; il motore non offre
                   nulla a cui legarlo, e un interruttore che non commuta niente è una bugia. */
                        showVisibilityIcon={false}
                        /* IL MIRINO DICE DOVE SEI: acceso su una riga sola, è la camera attraverso cui stai
                   guardando adesso. Premerlo ci porta la vista. */
                        rowToggle={(node) => ({
                            icon: <FrameSelectionIcon />,
                            label: T._('Look through this camera', 'Admin'),
                            active: playedCamera === node.id,
                            onClick: () => {
                                setSelectedIds([node.id]);
                                handlePlayCamera(node.id);
                            }
                        })}
                        flatList
                        /* L'ORDINE NON SI SALVA: una camera memorizzata ha sette campi e nessuno dice in che
                   posto sta nell'elenco. Trascinare mostrerebbe un ordine che al ricaricamento non
                   c'è più — e una cosa che si può fare e non resta è peggio di una che non si fa. */
                        disableDragAndDrop
                        onNodeMoved={() => {}}
                        disableOverlay={!!onInspectorChange}
                        onSelectedComponentChange={onInspectorChange}
                        /* Il nome è un campo suo: si scrive, e basta. */
                        onNodeRename={(node, name) => {
                            node.originalCameraLocation?.set?.('name', name);
                            updateNode(node.id, { name });
                        }}
                        rowActions={(_node, ids) => subjectCommands(ids)}
                    />
                </CamerasTree>
            </CamerasColumn>

            {/* Overlay per le impostazioni camera */}
            {showOverlay && !onInspectorChange && (
                <OverlayPanel onClose={closeCameraSettings}>
                    <div style={{ padding: '10px' }}>
                        <h3>{T._('Free camera', 'Admin')}</h3>
                        <CameraSettingOverlayComponent camera={cameraSettings} onPropertyChange={handleCameraPropertyChange} />
                    </div>
                </OverlayPanel>
            )}
        </SectionPanel>
    );
};

export default CameraEditor;
